import { TaskUtility } from 'src/app/task/task.utility';
import { Component, Input, OnInit } from '@angular/core';
import { IntlService } from '@progress/kendo-angular-intl';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { sortBy } from 'lodash';
import { NGXLogger } from 'ngx-logger';
import { LocalStorageService } from 'ngx-webstorage';
import { Channel } from 'src/app/channel/model/channel';
import { CaseService } from 'src/app/core/service/case.service';
import { ChannelService } from 'src/app/core/service/channel.service';
import { MemberService } from 'src/app/core/service/member.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Member } from 'src/app/member/model/member';
import { CaseTask } from '../model/case';
import { orderBy } from '@progress/kendo-data-query';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { TextProcessor } from 'src/app/core/utility/text.processor';
@Component({
  selector: 'app-case-member',
  templateUrl: './case-member.component.html',
  styleUrls: ['./case-member.component.scss']
})
export class CaseMemberComponent implements OnInit
{
  @Input() public memberId: number;

  public caseMemberCollapsed = false;
  public casesTasks: CaseTask[] = [];
  public communityEnabled = false;
  public taskCollapsed = true;
  public notEnrolledChannelsFiltered: any[] = [];
  public searchTerms = '';
  public channelToEnroll: Channel;
  public caseDialogOpened = false;
  public patientLabel = {};
  public member: Member;
  public collapseTasks = new Map();
  public groupedChannels: GroupResult[] = [];
  public task = null;
  public taskDueDateOpened = false;
  public priorityDialogOpened = false;
  public assigneeDialogOpened = false;
  public taskLoading = false;
  public distinctPriorities: Array<{ priorityName: string; priority: string }>;
  public autoComplete = false;
  public selectedTask: any = null;
  public taskOpened = false;
  public windowState = 'default';

  /**
   * The list of channels where this member has no open cases.
   */
  public notEnrolledChannels: Channel[];

  constructor(
    private caseService: CaseService,
    private settingService: SettingService,
    private messageHandlerService: MessageHandlerService,
    private logger: NGXLogger,
    private channelService: ChannelService,
    private memberService: MemberService,
    private storageService: LocalStorageService,
    private taskUtility: TaskUtility,
    public intl: IntlService
  )
  {
    if (this.storageService.retrieve('collapsible-section-case-member') === null)
    {
      this.storageService.store('collapsible-section-case-member', true);
    }
  }

  public ngOnInit(): void
  {
    this.distinctPriorities = this.taskUtility.distinctPriorities;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.communityEnabled = sessionSetting.communityEnabled;
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
    this.getMember();
    this.getChannelsData();
  }
  /**
   * Filters the channel staff based on user input.
   */
  public filterNotEnrolledChannels(filter: string): void
  {
    this.searchTerms = filter;
    this.notEnrolledChannelsFiltered = this.notEnrolledChannels
      .filter(channel => channel.name.toLowerCase().includes(filter.toLowerCase()));
    this.setGroupedChannels();
  }

  public getChannelsData(): void
  {
    this.getEnrolledChannels();
    this.getUnenrolledChannels();
    this.channelToEnroll = undefined;
  }

  public updateTaskCollapsed(caseId: number, hide: boolean): void
  {
    this.collapseTasks.set(caseId, hide);
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskDueDateDialog(task): void
  {
    this.task = task;
    this.taskDueDateOpened = true;
  }

  /**
   * Sets the selected task to null, and closes the task assignment dialog modal.
   */
  public closeTaskDueDateDialog(): void
  {
    this.task = null;
    this.taskDueDateOpened = false;
  }

  /**
   * Updates a task's due date.
   *
   * @param task Selected task for re-assignment.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskDueDate(task): void
  {
    this.task.dueDate = task.dueDate;
    this.taskDueDateOpened = false;
  }

  /**
   * Sets the selected task, and opens the task priority dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskPriorityDialog(task): void
  {
    this.task = task;
    this.priorityDialogOpened = true;
  }

  public closeTaskPriorityDialog(): void
  {
    this.priorityDialogOpened = false;
  }

  /**
   * Updates a task's priority.
   *
   * @param task Selected task for re-prioritize.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskPriority(task): void
  {
    this.task.priorityNumber = task.priorityNumber;
    this.task.priority = task.priority;
    this.task.priorityBadgeClass = this.taskUtility.priorityBadgeClass(task.priority);
    this.priorityDialogOpened = false;
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskAssigneeDialog(task): void
  {
    this.task = task;
    this.assigneeDialogOpened = true;
  }

  /**
   * Sets the selected task to null, and closes the task assignment dialog modal.
   */
  public closeTaskAssigneeDialog(): void
  {
    this.task = null;
    this.assigneeDialogOpened = false;
  }

  /**
   * Updates a task's assignee.
   *
   * @param task Selected task for re-assignment.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskAssignee(assignee): void
  {
    this.task.assigneeName = assignee.name;
    this.task.assignee = assignee.username;
    this.closeTaskAssigneeDialog();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent, grid: any, aCase: any): void
  {
    aCase.sort = state.sort;
    grid.data = orderBy(grid.data, aCase.sort);
  }

  /**
   * Opens the task detail modal.
   *
   * @param task Task object
   */
  public openTask(task: any, isAutoComplete = false): void
  {
    this.autoComplete = isAutoComplete;
    this.selectedTask = task;
    this.selectedTask.name = TextProcessor.truncateText(this.selectedTask.name, 70);
    this.taskOpened = true;
    document.body.style.overflow = 'hidden';
  }

  /**
   * Event handler when attempting to complete that and No was selected from the confirmation dialog.
   * If the complete dialog was automatically open (autoComplete=true) then the task detail dialog will
   * close automatically.
   */
  public closeTaskDetail(): void
  {
    this.closeTaskDialog();
  }

  /**
   * Event handler to close task dialog.
   */
  public closeTaskDialog(): void
  {
    this.taskOpened = false;
    document.body.style.overflow = '';
  }

  /**
   * Event handler for task completion.
   */
  public onTaskCompleted(): void
  {
    this.closeTaskDialog();
    this.getChannelsData();
  }

  public trackByCase(index: number, item: any): string
  {
    return item.id;
  }

  public cancelledCaseEnrollment(): void
  {
    this.caseDialogOpened = false;
    this.channelToEnroll = undefined;
  }

  private getMember(): void
  {
    this.memberService.getMember(this.memberId).subscribe({
      next: (data) =>
      {
        this.member = data;
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private getEnrolledChannels(): void
  {
    this.taskLoading = true;
    this.caseService.getCasesTasksByMember(this.memberId).subscribe({
      next: (cases: CaseTask[]) =>
      {
        cases = sortBy(cases, ['channel']);
        cases.forEach((d: any) =>
        {
          d.sort = [{ field: 'name', dir: 'asc' }];
          d.tasks.forEach((t: any) =>
          {
            t.openedOn = new Date(t.openedOn);
            t.dueDate = t.dueDate !== null ? new Date(t.dueDate) : null;
            t.priorityBadgeClass = this.taskUtility.priorityBadgeClass(t.priority);
            t.priority = this.taskUtility.priority(t.priority);
          });
        });

        this.casesTasks = cases;
        cases.forEach(c =>
        {
          this.collapseTasks.set(c.id, true);
        });
      },
      complete: () =>
      {
        this.taskLoading = false;
      }
    });
  }

  private getUnenrolledChannels(): void
  {
    this.channelService.getNotEnrolledChannels(this.memberId).subscribe({
      next: (data) =>
      {
        this.notEnrolledChannels = data;
        this.notEnrolledChannels.forEach(c =>
        {
          c.programName = c.program.name;
        });
        this.notEnrolledChannelsFiltered = this.notEnrolledChannels.sort((a, b) => (a.name.localeCompare(b.name)));
        this.setGroupedChannels();
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError(error);
      }
    });
  }

  private setGroupedChannels(): void
  {
    // Build the grouped Channels
    const groupedChannels = groupBy(this.notEnrolledChannelsFiltered, [{ field: 'programName' }]);
    this.groupedChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });
    this.groupedChannels = sortBy(groupedChannels, ['value']);
  }

}

